import { ref } from 'vue';
import { useAsyncState } from '@vueuse/core';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { api, legacyApi, utils } from '@dashboardApp/store';
import { normalizeStringOrNull } from '@dashboardApp/helpers/normalize';
import { mapTrendingIdeaToViewModel } from '@dashboardApp/helpers/mapTrendingIdeaToViewModel';
import { buildTrendingProductIdeasCategoryRoute } from '@dashboardApp/helpers/researchLinks';
import { useUserStore } from '@dashboardApp/stores/UserStore';

export const useProductIdeaStore = defineStore('ProductIdeaStore', () => {

    const userStore = useUserStore();
    const categorizedTrending = ref([]);
    const popularCategories = ref([]);

    const { execute: fetchCategorizedTrending, isReady: categorizedTrendingAreReady, isLoading: categorizedTrendingAreLoading } = useAsyncState(
        fetchCategorizedTrendingPromise,
        {},
        { immediate: false }
    );

    function fetchCategorizedTrendingPromise() {
        return legacyApi.get('/insights/trending/categorized').then(({ data }) => {
            categorizedTrending.value = Array.isArray(data)
                ? data.map((product) => normalizeCategorizedTrendingProduct(product))
                : [];
        });
    };

    const { execute: fetchPopularCategories, isReady: popularCategoriesAreReady, isLoading: popularCategoriesAreLoading } = useAsyncState(
        fetchPopularCategoriesPromise,
        {},
        { immediate: false }
    );

    function fetchPopularCategoriesPromise() {
        return legacyApi.get('/insights/categories/popular').then(({ data }) => {
            popularCategories.value = Array.isArray(data)
                ? data.map((category) => ({
                    ...category,
                    route: buildTrendingProductIdeasCategoryRoute(category?.slug),
                }))
                : [];
        });
    };

    /**
     * Save idea/report/product
     * 
     * @param {Object} product
     */
    function createReport(product) {
        const term = String(product?.name ?? product?.title ?? product?.term ?? '').trim();

        if (!term) {
            return Promise.resolve(null);
        }

        return api.post(
            'tracked-ideas',
            utils.normToJsonapi({
                _jv: {
                    type: 'tracked_ideas',
                },
                term,
            })
        ).then(({ data }) => {
            const record = data?.data ? utils.jsonapiToNorm(data.data) : null;
            const normalized = record ? {
                ...record,
                id: record?._jv?.id ?? null,
            } : null;

            if (normalized?.id) {
                userStore.bumpSession();
            }

            return normalized;
        });
    }

    /**
     * Remove idea/report/product
     * 
     * @param {Object} product
     */
    function deleteReportByProduct(product) {
        return legacyApi.delete(`/insights/reports/product/${product.id}`).then(({ data }) => {
            if (data) {
                userStore.bumpSession();
            }
            return data;
        });
    }

    /**
     * Remove idea/report by report id
     *
     * @param {Object} report
     */
    function deleteReport(report) {
        const reportId = normalizeStringOrNull(report?.id ?? report?.report_id);

        if (!reportId) {
            return Promise.resolve(false);
        }

        return legacyApi.delete(`/insights/reports/${reportId}`).then(({ data }) => {
            if (data) {
                userStore.bumpSession();
            }

            return data;
        });
    }

    return {
        categorizedTrending,
        popularCategories,
        fetchCategorizedTrending,
        fetchPopularCategories,
        categorizedTrendingAreReady,
        categorizedTrendingAreLoading,
        popularCategoriesAreReady,
        popularCategoriesAreLoading,
        createReport,
        deleteReport,
        deleteReportByProduct
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useProductIdeaStore, import.meta.hot));
}

function normalizeCategorizedTrendingProduct(product) {
    const mappedProduct = mapTrendingIdeaToViewModel(product);

    return {
        ...product,
        ...mappedProduct,
        name: normalizeStringOrNull(product?.name ?? product?.title ?? product?.term) || mappedProduct.name,
        thumbnail: normalizeStringOrNull(product?.thumbnail ?? product?.image_url) || mappedProduct.thumbnail,
        category: normalizeStringOrNull(product?.category) || mappedProduct.category,
        categoryRoute: mappedProduct.categoryRoute,
        reportRoute: mappedProduct.reportRoute,
        url: null,
        avgPrice: product?.avgPrice ?? mappedProduct.avgPrice,
        trend: product?.trend ?? mappedProduct.trend,
        humanTrend: product?.humanTrend ?? mappedProduct.humanTrend,
        trendValues: Array.isArray(product?.trendValues) ? product.trendValues : mappedProduct.trendValues,
        scores: product?.scores ?? mappedProduct.scores,
    };
}
